import {
  BILLING_PDF_ENDPOINT,
  ORG_BILLING_ENDPOINT_LOG
} from "../common/constants"
import { api } from "../common/utils"
import {
  AllOrgsBillingData,
  BillingEmpty,
  BillingLogs,
  BillingPdf
} from "../types"

/**
 * Fetch the system usage for an organization.
 * @param orgId The organization ID.
 * @param month Optional month parameter.
 * @returns System usage data for the organization.
 */
const fetchOrgSystemUsage = async (
  orgId: string | number,
  month?: string
): Promise<BillingLogs> => {
  try {
    const monthParam = month ? `&month=${month}` : ""
    const response = await api.get<BillingLogs | BillingEmpty>(
      `${ORG_BILLING_ENDPOINT_LOG}?org_id=${orgId}${monthParam}`
    )

    if (!response) {
      throw new Error(`Failed to fetch system usage for org ID ${orgId}`)
    }

    if ("status" in response.data && response.data.status === "error") {
      return []
    }

    if (Array.isArray(response.data)) {
      return response.data
    } else {
      throw new Error(`Unexpected response data for org ID ${orgId}`)
    }
  } catch (error) {
    console.error(`Error fetching system usage for org ID ${orgId}:`, error)
    throw error
  }
}

/**
 * Fetch system usage data for all organizations.
 * @param orgs Array of organizations.
 * @param month Optional month parameter.
 * @returns A mapping of organization IDs to system usage data.
 */
const fetchAllSystemUsage = async (
  orgs: { id: string }[],
  month?: string
): Promise<AllOrgsBillingData> => {
  const usageResponses = await Promise.all(
    orgs.map(async org => {
      const data = await fetchOrgSystemUsage(org.id, month)
      return { orgId: org.id, data }
    })
  )

  const usageMap: { [orgId: string]: BillingLogs } = {}
  usageResponses.forEach(({ orgId, data }) => {
    usageMap[orgId] = data
  })

  return usageMap
}

/**
 * Fetch billing PDF for an invoice of an organization.
 * @param org_id Organization ID.
 * @param invoice Invoice ID.
 * @returns BillingPdf
 */
const fetchBillingPdf = async (
  org_id: number,
  invoice: string
): Promise<BillingPdf> => {
  try {
    const response = await api.get<BillingPdf>(
      `${BILLING_PDF_ENDPOINT}?org_id=${org_id}&invoice=${invoice}`
    )

    return response.data
  } catch (error) {
    throw error
  }
}
const billing = {
  fetchOrgSystemUsage,
  fetchAllSystemUsage,
  fetchBillingPdf
}

export default billing
